import { useEffect, useRef, useState, type ChangeEvent } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  ClickAwayListener,
  Collapse,
  Container,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import {
  questionBombItems,
  questionBombMissions,
  questionBombModes,
  type QuestionBombCustomMission,
  type QuestionBombCustomItem,
  type QuestionBombItem,
  type QuestionBombMode,
  type QuestionBombModeId,
} from "../data/questionBombItems";
import { shuffleArray } from "../utils/shuffleArray";
import {
  loadQuestionBombStorage,
  saveQuestionBombStorage,
} from "../utils/questionBombStorage";

type DisplayCard =
  | { type: "question"; questionId: string }
  | { type: "mission"; missionId: string }
  | null;

const DEFAULT_CUSTOM_CATEGORY = "";
const CUSTOM_MODE_ID: QuestionBombModeId = "customOnly";
const CUSTOM_MISSION_CHIP_LABEL = "직접 추가";
const CUSTOM_MODE_MISSION_INTERVAL_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];
const RECENT_QUESTION_LIMIT = 8;
const MODE_TOOLTIP_LONG_PRESS_MS = 550;
const singleLineChipRowSx = {
  flexWrap: "nowrap",
  overflowX: "auto",
  overflowY: "hidden",
  pb: 0.25,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
};
const mainQuestionBombModes = questionBombModes.filter(
  (mode) => !mode.settingsOnly,
);
const nestedLibraryAccordionSx = {
  borderRadius: 2,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  "&:before": { display: "none" },
  overflow: "hidden",
};

type SectionInfoTooltipId = "customQuestion" | "customMission" | null;

type AddSectionHeaderProps = {
  infoId: Exclude<SectionInfoTooltipId, null>;
  title: string;
  tooltipText: string;
  openTooltipId: SectionInfoTooltipId;
  onOpen: (tooltipId: Exclude<SectionInfoTooltipId, null>) => void;
  onClose: () => void;
  onToggle: (tooltipId: Exclude<SectionInfoTooltipId, null>) => void;
};

function AddSectionHeader({
  infoId,
  title,
  tooltipText,
  openTooltipId,
  onOpen,
  onClose,
  onToggle,
}: AddSectionHeaderProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <ClickAwayListener onClickAway={onClose}>
        <Tooltip
          arrow
          placement="top"
          open={openTooltipId === infoId}
          disableTouchListener
          disableInteractive
          title={
            <Typography variant="body2" sx={{ maxWidth: 220 }}>
              {tooltipText}
            </Typography>
          }
        >
          <IconButton
            size="small"
            aria-label={`${title} 안내`}
            onMouseEnter={() => onOpen(infoId)}
            onMouseLeave={onClose}
            onFocus={() => onOpen(infoId)}
            onBlur={onClose}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onToggle(infoId);
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ClickAwayListener>
    </Stack>
  );
}

const getModeById = (modeId: QuestionBombModeId) =>
  questionBombModes.find((mode) => mode.id === modeId) ?? questionBombModes[0];

const getMissionEveryForMode = (
  modeId: QuestionBombModeId,
  customModeMissionsEnabled: boolean,
  customModeMissionEvery: number,
) => {
  if (modeId !== CUSTOM_MODE_ID) {
    return getModeById(modeId).missionEvery;
  }

  return customModeMissionsEnabled ? customModeMissionEvery : null;
};

const hasMissionRounds = (missionEvery: number | null, missionCount: number) =>
  missionEvery !== null && missionCount > 0;

const getMissionChipLabel = (
  missionEvery: number | null,
  hasMissionMode: boolean,
  missionReady: boolean,
  questionsUntilMission: number | null,
) => {
  if (!hasMissionMode) return "미션 없음";
  if (missionReady) return "다음 카드는 미션";

  return `미션까지 질문 ${questionsUntilMission ?? missionEvery ?? 0}개`;
};

const getModeRuleSummary = (
  mode: QuestionBombMode,
  missionEvery: number | null,
  missionCount: number,
) => {
  if (mode.id !== CUSTOM_MODE_ID) {
    return mode.ruleSummary;
  }

  if (missionEvery === null) {
    return "사용자 질문만 이어지고 사용자 미션 카드는 나오지 않습니다.";
  }

  if (missionCount === 0) {
    return `사용자 질문만 이어지고, 미션을 추가해 두면 질문 ${missionEvery}개마다 사용자 미션 카드가 등장합니다.`;
  }

  return `사용자 질문만 이어지고, 사용자 미션 카드가 질문 ${missionEvery}개마다 등장합니다.`;
};

const getQuestionsForMode = (
  modeId: QuestionBombModeId,
  customQuestions: QuestionBombCustomItem[],
) =>
  modeId === CUSTOM_MODE_ID
    ? customQuestions
    : questionBombItems.filter((item) => item.modeId === modeId);

const getMissionsForMode = (
  modeId: QuestionBombModeId,
  customMissions: QuestionBombCustomMission[],
) =>
  modeId === CUSTOM_MODE_ID
    ? customMissions
    : questionBombMissions.filter((mission) => mission.modeId === modeId);

const getRecentQuestionIdsForQuestions = (
  questions: Array<QuestionBombItem | QuestionBombCustomItem>,
  recentQuestionIds: string[],
) => {
  const questionIdSet = new Set(questions.map((item) => item.id));

  return recentQuestionIds.filter((id) => questionIdSet.has(id));
};

const createQuestionOrder = (
  questions: Array<QuestionBombItem | QuestionBombCustomItem>,
  recentQuestionIds: string[],
  excludeRecent: boolean,
) => {
  const questionIds = questions.map((item) => item.id);

  if (!excludeRecent || questionIds.length <= 1) {
    return shuffleArray(questionIds);
  }

  const recentQuestionSet = new Set(recentQuestionIds);
  const freshQuestionIds = questionIds.filter(
    (id) => !recentQuestionSet.has(id),
  );
  const fallbackQuestionIds = questionIds.filter((id) =>
    recentQuestionSet.has(id),
  );

  if (freshQuestionIds.length === 0) {
    return shuffleArray(questionIds);
  }

  return [
    ...shuffleArray(freshQuestionIds),
    ...shuffleArray(fallbackQuestionIds),
  ];
};

const createMissionOrder = (
  modeId: QuestionBombModeId,
  customMissions: QuestionBombCustomMission[],
) =>
  shuffleArray(
    getMissionsForMode(modeId, customMissions).map((mission) => mission.id),
  );

export default function QuestionBombPage() {
  const navigate = useNavigate();
  const [initialStorage] = useState(() => loadQuestionBombStorage());
  const initialQuestions = getQuestionsForMode(
    initialStorage.selectedModeId,
    initialStorage.customQuestions,
  );
  const initialRecentQuestionIds = getRecentQuestionIdsForQuestions(
    initialQuestions,
    initialStorage.recentQuestionIds,
  );
  const initialMissionEvery = getMissionEveryForMode(
    initialStorage.selectedModeId,
    initialStorage.customModeMissionsEnabled,
    initialStorage.customModeMissionEvery,
  );
  const modeTooltipTimerRef = useRef<number | null>(null);
  const modeTooltipOpenedByTouchRef = useRef(false);
  const ignoreTooltipFocusRef = useRef(false);
  const skipModeClickRef = useRef(false);
  const skipModeClickTimerRef = useRef<number | null>(null);

  const [selectedModeId, setSelectedModeId] = useState<QuestionBombModeId>(
    initialStorage.selectedModeId,
  );
  const [excludeRecent, setExcludeRecent] = useState(
    initialStorage.excludeRecent,
  );
  const [favoriteQuestionIds, setFavoriteQuestionIds] = useState<string[]>(
    initialStorage.favoriteQuestionIds,
  );
  const [recentQuestionIds, setRecentQuestionIds] = useState<string[]>(
    initialStorage.recentQuestionIds,
  );
  const [customQuestions, setCustomQuestions] = useState<
    QuestionBombCustomItem[]
  >(initialStorage.customQuestions);
  const [customMissions, setCustomMissions] = useState<
    QuestionBombCustomMission[]
  >(initialStorage.customMissions);
  const [customModeMissionsEnabled, setCustomModeMissionsEnabled] = useState(
    initialStorage.customModeMissionsEnabled,
  );
  const [customModeMissionEvery, setCustomModeMissionEvery] = useState(
    initialStorage.customModeMissionEvery,
  );
  const [questionOrder, setQuestionOrder] = useState<string[]>(() =>
    createQuestionOrder(
      initialQuestions,
      initialRecentQuestionIds,
      initialStorage.excludeRecent,
    ),
  );
  const [missionOrder, setMissionOrder] = useState<string[]>(() =>
    createMissionOrder(
      initialStorage.selectedModeId,
      initialStorage.customMissions,
    ),
  );
  const [currentCard, setCurrentCard] = useState<DisplayCard>(null);
  const [questionCursor, setQuestionCursor] = useState(0);
  const [missionCursor, setMissionCursor] = useState(0);
  const [missionCount, setMissionCount] = useState(0);
  const [questionsUntilMission, setQuestionsUntilMission] = useState<
    number | null
  >(initialMissionEvery);
  const [customModeId, setCustomModeId] =
    useState<QuestionBombModeId>(CUSTOM_MODE_ID);
  const [customCategory, setCustomCategory] = useState(DEFAULT_CUSTOM_CATEGORY);
  const [customText, setCustomText] = useState("");
  const [customFormError, setCustomFormError] = useState("");
  const [customMissionTitle, setCustomMissionTitle] = useState("");
  const [customMissionDescription, setCustomMissionDescription] = useState("");
  const [customMissionNote, setCustomMissionNote] = useState("");
  const [customMissionFormError, setCustomMissionFormError] = useState("");
  const [isLibraryExpanded, setIsLibraryExpanded] = useState(false);
  const [isCustomQuestionLibraryExpanded, setIsCustomQuestionLibraryExpanded] =
    useState(false);
  const [isCustomQuestionListExpanded, setIsCustomQuestionListExpanded] =
    useState(false);
  const [isCustomQuestionFormExpanded, setIsCustomQuestionFormExpanded] =
    useState(false);
  const [isCustomMissionLibraryExpanded, setIsCustomMissionLibraryExpanded] =
    useState(false);
  const [isCustomMissionListExpanded, setIsCustomMissionListExpanded] =
    useState(false);
  const [isCustomMissionFormExpanded, setIsCustomMissionFormExpanded] =
    useState(false);
  const [isRecentExcludeInfoOpen, setIsRecentExcludeInfoOpen] = useState(false);
  const [openSectionInfoTooltipId, setOpenSectionInfoTooltipId] =
    useState<SectionInfoTooltipId>(null);
  const [openModeTooltipId, setOpenModeTooltipId] =
    useState<QuestionBombModeId | null>(null);

  const activeMode = getModeById(selectedModeId);
  const activeMissionEvery = getMissionEveryForMode(
    selectedModeId,
    customModeMissionsEnabled,
    customModeMissionEvery,
  );
  const allQuestions = [...questionBombItems, ...customQuestions];
  const allMissions = [...questionBombMissions, ...customMissions];
  const activeQuestions = getQuestionsForMode(selectedModeId, customQuestions);
  const activeMissions = getMissionsForMode(selectedModeId, customMissions);
  const activeRecentQuestionIds = getRecentQuestionIdsForQuestions(
    activeQuestions,
    recentQuestionIds,
  );
  const activeRecentQuestions = activeRecentQuestionIds
    .map((id) => activeQuestions.find((item) => item.id === id))
    .filter(
      (item): item is QuestionBombItem | QuestionBombCustomItem =>
        item !== undefined,
    );
  const activeModeHasMissions = hasMissionRounds(
    activeMissionEvery,
    activeMissions.length,
  );
  const activeModeRuleSummary = getModeRuleSummary(
    activeMode,
    activeMissionEvery,
    activeMissions.length,
  );
  const currentQuestion =
    currentCard?.type === "question"
      ? (allQuestions.find((item) => item.id === currentCard.questionId) ??
        null)
      : null;
  const currentMission =
    currentCard?.type === "mission"
      ? (allMissions.find((mission) => mission.id === currentCard.missionId) ??
        null)
      : null;
  const favoriteQuestions = favoriteQuestionIds
    .map((id) => allQuestions.find((item) => item.id === id))
    .filter(
      (item): item is QuestionBombItem | QuestionBombCustomItem =>
        item !== undefined,
    );
  const isCurrentQuestionFavorite = currentQuestion
    ? favoriteQuestionIds.includes(currentQuestion.id)
    : false;
  const hasMoreQuestions = questionCursor < questionOrder.length;
  const missionReady = activeModeHasMissions && questionsUntilMission === 0;
  const isCompleted = !hasMoreQuestions && !missionReady;
  const currentRound = currentCard ? questionCursor + missionCount : 0;
  const remainingQuestionCount = Math.max(
    questionOrder.length - questionCursor,
    0,
  );
  const activeModeCustomCount =
    selectedModeId === CUSTOM_MODE_ID ? customQuestions.length : 0;
  const activeModeFavoriteCount = favoriteQuestions.filter(
    (item) => item.modeId === selectedModeId,
  ).length;

  useEffect(() => {
    saveQuestionBombStorage({
      selectedModeId,
      excludeRecent,
      favoriteQuestionIds,
      recentQuestionIds,
      customQuestions,
      customMissions,
      customModeMissionsEnabled,
      customModeMissionEvery,
    });
  }, [
    selectedModeId,
    excludeRecent,
    favoriteQuestionIds,
    recentQuestionIds,
    customQuestions,
    customMissions,
    customModeMissionsEnabled,
    customModeMissionEvery,
  ]);

  useEffect(() => {
    return () => {
      if (modeTooltipTimerRef.current) {
        window.clearTimeout(modeTooltipTimerRef.current);
      }
      if (skipModeClickTimerRef.current) {
        window.clearTimeout(skipModeClickTimerRef.current);
      }
    };
  }, []);

  const clearModeTooltipTimer = () => {
    if (!modeTooltipTimerRef.current) return;
    window.clearTimeout(modeTooltipTimerRef.current);
    modeTooltipTimerRef.current = null;
  };

  const scheduleSkipModeClickReset = () => {
    if (skipModeClickTimerRef.current) {
      window.clearTimeout(skipModeClickTimerRef.current);
    }

    skipModeClickTimerRef.current = window.setTimeout(() => {
      skipModeClickRef.current = false;
      skipModeClickTimerRef.current = null;
    }, 400);
  };

  const openModeTooltip = (modeId: QuestionBombModeId) => {
    setOpenModeTooltipId(modeId);
  };

  const closeModeTooltip = () => {
    clearModeTooltipTimer();
    setOpenModeTooltipId(null);
  };

  const releaseTooltipFocusGuard = () => {
    window.setTimeout(() => {
      ignoreTooltipFocusRef.current = false;
    }, 400);
  };

  const handleModeTooltipTouchStart = (modeId: QuestionBombModeId) => {
    ignoreTooltipFocusRef.current = true;
    modeTooltipOpenedByTouchRef.current = false;
    clearModeTooltipTimer();

    modeTooltipTimerRef.current = window.setTimeout(() => {
      modeTooltipOpenedByTouchRef.current = true;
      skipModeClickRef.current = true;
      openModeTooltip(modeId);
      modeTooltipTimerRef.current = null;
    }, MODE_TOOLTIP_LONG_PRESS_MS);
  };

  const handleModeTooltipTouchEnd = () => {
    clearModeTooltipTimer();

    if (modeTooltipOpenedByTouchRef.current) {
      closeModeTooltip();
      modeTooltipOpenedByTouchRef.current = false;
      scheduleSkipModeClickReset();
    }

    releaseTooltipFocusGuard();
  };

  const handleModeTooltipTouchCancel = () => {
    clearModeTooltipTimer();

    if (modeTooltipOpenedByTouchRef.current) {
      closeModeTooltip();
      modeTooltipOpenedByTouchRef.current = false;
      scheduleSkipModeClickReset();
    }

    releaseTooltipFocusGuard();
  };

  const rebuildSession = ({
    modeId = selectedModeId,
    nextCustomQuestions = customQuestions,
    nextCustomMissions = customMissions,
    nextRecentQuestionIds = recentQuestionIds,
    nextExcludeRecent = excludeRecent,
    nextCustomModeMissionsEnabled = customModeMissionsEnabled,
    nextCustomModeMissionEvery = customModeMissionEvery,
  }: {
    modeId?: QuestionBombModeId;
    nextCustomQuestions?: QuestionBombCustomItem[];
    nextCustomMissions?: QuestionBombCustomMission[];
    nextRecentQuestionIds?: string[];
    nextExcludeRecent?: boolean;
    nextCustomModeMissionsEnabled?: boolean;
    nextCustomModeMissionEvery?: number;
  } = {}) => {
    const nextMissionEvery = getMissionEveryForMode(
      modeId,
      nextCustomModeMissionsEnabled,
      nextCustomModeMissionEvery,
    );
    const nextQuestions = getQuestionsForMode(modeId, nextCustomQuestions);
    const nextModeRecentQuestionIds = getRecentQuestionIdsForQuestions(
      nextQuestions,
      nextRecentQuestionIds,
    );

    setQuestionOrder(
      createQuestionOrder(
        nextQuestions,
        nextModeRecentQuestionIds,
        nextExcludeRecent,
      ),
    );
    setMissionOrder(createMissionOrder(modeId, nextCustomMissions));
    setCurrentCard(null);
    setQuestionCursor(0);
    setMissionCursor(0);
    setMissionCount(0);
    setQuestionsUntilMission(nextMissionEvery);
  };

  const resetSession = () => {
    setCurrentCard(null);
    setQuestionCursor(0);
    setMissionCursor(0);
    setMissionCount(0);
    setQuestionsUntilMission(activeMissionEvery);
  };

  const handleLibraryExpandedChange = (expanded: boolean) => {
    setIsLibraryExpanded(expanded);

    if (!expanded) return;

    setIsCustomQuestionLibraryExpanded(false);
    setIsCustomQuestionListExpanded(false);
    setIsCustomQuestionFormExpanded(false);
    setIsCustomMissionLibraryExpanded(false);
    setIsCustomMissionListExpanded(false);
    setIsCustomMissionFormExpanded(false);
  };

  const toggleFavorite = () => {
    if (!currentQuestion) return;

    setFavoriteQuestionIds((previousIds) =>
      previousIds.includes(currentQuestion.id)
        ? previousIds.filter((id) => id !== currentQuestion.id)
        : [currentQuestion.id, ...previousIds],
    );
  };

  const removeFavorite = (questionId: string) => {
    setFavoriteQuestionIds((previousIds) =>
      previousIds.filter((id) => id !== questionId),
    );
  };

  const drawMissionCard = () => {
    if (activeMissions.length === 0) return;

    let nextMissionOrder = missionOrder;
    let nextMissionCursor = missionCursor;

    if (nextMissionCursor >= nextMissionOrder.length) {
      nextMissionOrder = createMissionOrder(selectedModeId, customMissions);
      nextMissionCursor = 0;
      setMissionOrder(nextMissionOrder);
    }

    const nextMissionId = nextMissionOrder[nextMissionCursor];
    if (!nextMissionId) return;

    setCurrentCard({ type: "mission", missionId: nextMissionId });
    setMissionCursor(nextMissionCursor + 1);
    setMissionCount((value) => value + 1);
    setQuestionsUntilMission(activeMissionEvery);
  };

  const drawQuestionCard = () => {
    const nextQuestionId = questionOrder[questionCursor];
    if (!nextQuestionId) return;

    setCurrentCard({ type: "question", questionId: nextQuestionId });
    setQuestionCursor((value) => value + 1);
    setQuestionsUntilMission((value) => {
      if (!activeModeHasMissions || value === null) return null;
      return Math.max(value - 1, 0);
    });
    setRecentQuestionIds((previousIds) =>
      [
        nextQuestionId,
        ...previousIds.filter((id) => id !== nextQuestionId),
      ].slice(0, RECENT_QUESTION_LIMIT),
    );
  };

  const drawNextCard = () => {
    if (isCompleted) return;

    if (missionReady) {
      drawMissionCard();
      return;
    }

    drawQuestionCard();
  };

  const handleModeChange = (modeId: QuestionBombModeId) => {
    if (modeId === selectedModeId) return;

    closeModeTooltip();
    setSelectedModeId(modeId);
    rebuildSession({ modeId });
  };

  const handleExcludeRecentChange = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setExcludeRecent(checked);
    rebuildSession({ nextExcludeRecent: checked });
  };

  const handleCustomModeMissionsEnabledChange = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setCustomModeMissionsEnabled(checked);
    rebuildSession({
      nextCustomModeMissionsEnabled: checked,
    });
  };

  const handleCustomModeMissionEveryChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const nextMissionEvery = Number(event.target.value);

    if (!Number.isInteger(nextMissionEvery)) return;

    setCustomModeMissionEvery(nextMissionEvery);
    rebuildSession({
      nextCustomModeMissionEvery: nextMissionEvery,
    });
  };

  const openSectionInfoTooltip = (
    tooltipId: Exclude<SectionInfoTooltipId, null>,
  ) => {
    setOpenSectionInfoTooltipId(tooltipId);
  };

  const closeSectionInfoTooltip = () => {
    setOpenSectionInfoTooltipId(null);
  };

  const toggleSectionInfoTooltip = (
    tooltipId: Exclude<SectionInfoTooltipId, null>,
  ) => {
    setOpenSectionInfoTooltipId((previousId) =>
      previousId === tooltipId ? null : tooltipId,
    );
  };

  const handleAddCustomQuestion = () => {
    const trimmedText = customText.trim();
    const trimmedCategory = customCategory.trim();

    if (trimmedText.length < 6) {
      setCustomFormError("질문은 최소 6자 이상 입력해 주세요.");
      return;
    }

    const nextCustomQuestion: QuestionBombCustomItem = {
      id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      modeId: CUSTOM_MODE_ID,
      category: trimmedCategory,
      text: trimmedText,
      createdAt: Date.now(),
    };
    const nextCustomQuestions = [nextCustomQuestion, ...customQuestions];

    setCustomQuestions(nextCustomQuestions);
    setCustomCategory(DEFAULT_CUSTOM_CATEGORY);
    setCustomText("");
    setCustomFormError("");
    setIsLibraryExpanded(true);

    if (selectedModeId === CUSTOM_MODE_ID) {
      setQuestionOrder((previousOrder) => {
        const usedQuestionIds = previousOrder.slice(0, questionCursor);
        const remainingQuestionIds = previousOrder.slice(questionCursor);

        return [
          ...usedQuestionIds,
          ...shuffleArray([...remainingQuestionIds, nextCustomQuestion.id]),
        ];
      });
    }
  };

  const handleAddCustomMission = () => {
    const trimmedTitle = customMissionTitle.trim();
    const trimmedDescription = customMissionDescription.trim();
    const trimmedNote = customMissionNote.trim();

    if (trimmedTitle.length < 2) {
      setCustomMissionFormError("미션 제목은 최소 2자 이상 입력해 주세요.");
      return;
    }

    if (trimmedDescription.length < 4) {
      setCustomMissionFormError("미션 설명은 최소 4자 이상 입력해 주세요.");
      return;
    }

    const nextCustomMission: QuestionBombCustomMission = {
      id: `custom-mission-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
      modeId: CUSTOM_MODE_ID,
      title: trimmedTitle,
      description: trimmedDescription,
      note: trimmedNote,
      createdAt: Date.now(),
    };
    const nextCustomMissions = [nextCustomMission, ...customMissions];

    setCustomMissions(nextCustomMissions);
    setCustomMissionTitle("");
    setCustomMissionDescription("");
    setCustomMissionNote("");
    setCustomMissionFormError("");
    setIsLibraryExpanded(true);

    if (selectedModeId === CUSTOM_MODE_ID) {
      setMissionOrder((previousOrder) => {
        const usedMissionIds = previousOrder.slice(0, missionCursor);
        const remainingMissionIds = previousOrder.slice(missionCursor);

        return [
          ...usedMissionIds,
          ...shuffleArray([...remainingMissionIds, nextCustomMission.id]),
        ];
      });
    }
  };

  const handleRemoveCustomQuestion = (questionId: string) => {
    const nextCustomQuestions = customQuestions.filter(
      (item) => item.id !== questionId,
    );
    const nextRecentQuestionIds = recentQuestionIds.filter(
      (id) => id !== questionId,
    );

    setCustomQuestions(nextCustomQuestions);
    setFavoriteQuestionIds((previousIds) =>
      previousIds.filter((id) => id !== questionId),
    );
    setRecentQuestionIds(nextRecentQuestionIds);

    if (selectedModeId === CUSTOM_MODE_ID) {
      rebuildSession({
        nextCustomQuestions,
        nextRecentQuestionIds,
      });
    }
  };

  const handleRemoveCustomMission = (missionId: string) => {
    const nextCustomMissions = customMissions.filter(
      (mission) => mission.id !== missionId,
    );

    setCustomMissions(nextCustomMissions);

    if (selectedModeId === CUSTOM_MODE_ID) {
      rebuildSession({
        nextCustomMissions,
      });
    }
  };

  const nextDrawLabel = isCompleted
    ? "다시 섞어서 처음부터"
    : !currentCard
      ? "질문 뽑기"
      : missionReady
        ? "미션 카드 뽑기"
        : "다음 질문 뽑기";
  const missionChipLabel = getMissionChipLabel(
    activeMissionEvery,
    activeModeHasMissions,
    missionReady,
    questionsUntilMission,
  );

  return (
    <Container maxWidth="sm">
      <Paper elevation={4} sx={{ p: 3, mt: 4, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack spacing={1} textAlign="center">
            <Typography variant="h4">💥 랜덤 질문 폭탄</Typography>
            <Typography color="text.secondary">
              모드마다 질문 톤과 미션 주기가 달라져서 같은 게임도 다른 흐름으로
              즐길 수 있어요.
            </Typography>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                플레이 모드
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={singleLineChipRowSx}
              >
                {mainQuestionBombModes.map((mode) => (
                  <Tooltip
                    key={mode.id}
                    arrow
                    placement="top"
                    open={openModeTooltipId === mode.id}
                    TransitionProps={{ timeout: 0 }}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    title={
                      <Stack spacing={0.5} sx={{ maxWidth: 240 }}>
                        <Typography variant="subtitle2">
                          {mode.label}
                        </Typography>
                        <Typography variant="body2">
                          {mode.description}
                        </Typography>
                        <Typography variant="body2">
                          {mode.ruleSummary}
                        </Typography>
                      </Stack>
                    }
                  >
                    <Button
                      size="small"
                      disableRipple
                      aria-label={mode.shortLabel}
                      variant={
                        mode.id === selectedModeId ? "contained" : "outlined"
                      }
                      color={mode.accentColor}
                      onClick={() => {
                        if (skipModeClickRef.current) {
                          skipModeClickRef.current = false;
                          if (skipModeClickTimerRef.current) {
                            window.clearTimeout(skipModeClickTimerRef.current);
                            skipModeClickTimerRef.current = null;
                          }
                          return;
                        }

                        handleModeChange(mode.id);
                      }}
                      onMouseEnter={() => openModeTooltip(mode.id)}
                      onMouseLeave={closeModeTooltip}
                      onFocus={() => {
                        if (ignoreTooltipFocusRef.current) return;
                        openModeTooltip(mode.id);
                      }}
                      onBlur={closeModeTooltip}
                      onTouchStart={() => handleModeTooltipTouchStart(mode.id)}
                      onTouchEnd={handleModeTooltipTouchEnd}
                      onTouchMove={handleModeTooltipTouchCancel}
                      onTouchCancel={handleModeTooltipTouchCancel}
                      sx={{
                        flex: {
                          xs: "1 1 calc(50% - 8px)",
                          sm: "1 1 calc(25% - 8px)",
                        },
                        minWidth: 0,
                      }}
                    >
                      {mode.shortLabel}
                    </Button>
                  </Tooltip>
                ))}
              </Stack>

              <Typography variant="body2" color="text.secondary">
                모드 버튼에 마우스를 올리거나, 모바일에서는 버튼을 꾹 누르고
                있을 때 설명을 확인할 수 있어요.
              </Typography>
              {activeMode.settingsOnly && (
                <Typography variant="body2" color="text.secondary">
                  현재는 설정 전용인 사용자 설정 모드로 플레이 중이에요. 기본
                  모드는 여기서, 사용자 설정 모드는 아래 저장 & 설정에서 다시
                  고를 수 있어요.
                </Typography>
              )}
            </Stack>
          </Paper>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={singleLineChipRowSx}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`질문 ${questionCursor} / ${questionOrder.length}`}
            />
            <Chip
              size="small"
              color={missionReady ? "warning" : "default"}
              label={missionChipLabel}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`즐겨찾기 ${activeModeFavoriteCount}개`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`내 질문 ${activeModeCustomCount}개`}
            />
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              minHeight: 260,
              borderRadius: 3,
              borderColor:
                currentCard?.type === "mission" ? "warning.main" : "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {currentQuestion ? (
              <Stack spacing={2} sx={{ width: "100%" }}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={singleLineChipRowSx}
                  >
                    {currentQuestion.category.trim().length > 0 && (
                      <Chip
                        size="small"
                        color={activeMode.accentColor}
                        label={currentQuestion.category}
                      />
                    )}
                    {"createdAt" in currentQuestion && (
                      <Chip size="small" variant="outlined" label="내 질문" />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`ROUND ${currentRound}`}
                    />
                  </Stack>

                  <IconButton
                    aria-label="현재 질문 즐겨찾기"
                    onClick={toggleFavorite}
                    sx={{
                      color: isCurrentQuestionFavorite
                        ? "warning.main"
                        : "text.secondary",
                    }}
                  >
                    {isCurrentQuestionFavorite ? (
                      <BookmarkIcon />
                    ) : (
                      <BookmarkBorderIcon />
                    )}
                  </IconButton>
                </Stack>

                <Typography variant="h5" textAlign="center">
                  {currentQuestion.text}
                </Typography>

                <Typography textAlign="center" color="text.secondary">
                  {activeMode.answerGuide}
                </Typography>
              </Stack>
            ) : currentMission ? (
              <Stack spacing={1.75} textAlign="center">
                <Chip
                  size="small"
                  color="warning"
                  icon={<AutoAwesomeIcon />}
                  label={`MISSION ROUND ${currentRound}`}
                  sx={{ alignSelf: "center" }}
                />
                <Typography variant="h5">{currentMission.title}</Typography>
                <Typography>{currentMission.description}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentMission.note}
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.5} textAlign="center">
                <Typography variant="h6">아직 카드를 뽑지 않았어요.</Typography>
                <Typography color="text.secondary">
                  {activeMode.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeModeRuleSummary}
                </Typography>
              </Stack>
            )}
          </Paper>

          {questionCursor > 0 && (
            <Typography textAlign="center" color="text.secondary">
              남은 질문 {remainingQuestionCount}개
            </Typography>
          )}

          {isCompleted && (
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 3, textAlign: "center" }}
            >
              <Stack spacing={1}>
                <Typography fontWeight={600}>
                  현재 모드의 질문을 모두 다 사용했어요.
                </Typography>
                <Typography color="text.secondary">
                  다시 섞어서 새 순서로 이어가거나, 다른 모드로 바꿔 다른 톤의
                  질문을 바로 시작할 수 있어요.
                </Typography>
              </Stack>
            </Paper>
          )}

          <Stack spacing={2.5}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color={isCompleted ? activeMode.accentColor : "primary"}
              onClick={isCompleted ? () => rebuildSession() : drawNextCard}
              disabled={!isCompleted && activeQuestions.length === 0}
            >
              {nextDrawLabel}
            </Button>

            <Accordion
              expanded={isLibraryExpanded}
              onChange={(_, expanded) =>
                handleLibraryExpandedChange(expanded)
              }
              disableGutters
              sx={{
                borderRadius: 3,
                bgcolor: "background.default",
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label="저장 & 설정"
              >
                <Stack spacing={0.25}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    저장 & 설정
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    즐겨찾기 {favoriteQuestions.length}개, 사용자 정의 질문{" "}
                    {customQuestions.length}개가 새로고침 후에도 유지됩니다.
                  </Typography>
                </Stack>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={2.5}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={excludeRecent}
                            onChange={handleExcludeRecentChange}
                          />
                        }
                        label="최근 본 질문 우선 제외"
                      />
                      <Typography variant="body2" color="text.secondary">
                        최근 {RECENT_QUESTION_LIMIT}개 질문은 먼저 뒤로 보내고,
                        새 질문이 부족할 때만 다시 섞어서 이어갑니다.
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        onClick={() =>
                          setIsRecentExcludeInfoOpen((previous) => !previous)
                        }
                        endIcon={
                          <ExpandMoreIcon
                            sx={{
                              transition: "transform 0.2s ease",
                              transform: isRecentExcludeInfoOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            }}
                          />
                        }
                        sx={{
                          alignSelf: "flex-start",
                          minHeight: 0,
                          px: 0,
                          py: 0,
                          color: "text.secondary",
                          fontSize: (theme) =>
                            theme.typography.caption.fontSize,
                          fontWeight: 500,
                          textTransform: "none",
                          "& .MuiButton-endIcon": {
                            ml: 0.25,
                          },
                        }}
                      >
                        최근 {RECENT_QUESTION_LIMIT}개가 뭐예요?
                      </Button>
                      <Collapse in={isRecentExcludeInfoOpen} unmountOnExit>
                        <Stack spacing={0.5} sx={{ pt: 0.25, pl: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">
                            현재 모드에서 최근에 나온 질문을 최대{" "}
                            {RECENT_QUESTION_LIMIT}개까지 기억해요.
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            완전히 제외하는 건 아니고 먼저 뒤로 미뤘다가, 새
                            질문이 부족하면 다시 나올 수 있어요.
                          </Typography>
                          <Stack spacing={0.5} sx={{ pt: 0.25 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              현재 기록 질문
                            </Typography>
                            {activeRecentQuestions.length > 0 ? (
                              <Stack
                                spacing={0.5}
                                sx={{
                                  maxHeight: 132,
                                  overflowY: "auto",
                                  pr: 0.5,
                                  py: 0.75,
                                  px: 1,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1.5,
                                  bgcolor: "background.paper",
                                }}
                              >
                                {activeRecentQuestions.map(
                                  (question, index) => (
                                    <Typography
                                      key={question.id}
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ lineHeight: 1.45 }}
                                    >
                                      {index + 1}. {question.text}
                                    </Typography>
                                  ),
                                )}
                              </Stack>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                아직 최근 기록이 없어요. 질문을 뽑으면 여기에
                                표시돼요.
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Collapse>
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          현재 선택 모드
                        </Typography>
                        <Chip
                          size="small"
                          color={activeMode.accentColor}
                          label={activeMode.shortLabel}
                        />
                      </Stack>
                      <Typography fontWeight={600}>
                        {activeMode.label}
                      </Typography>
                      <Typography color="text.secondary">
                        {activeMode.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activeModeRuleSummary}
                      </Typography>
                      {activeMode.id === CUSTOM_MODE_ID && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "background.default",
                          }}
                        >
                          <Stack spacing={1.25}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={customModeMissionsEnabled}
                                  onChange={
                                    handleCustomModeMissionsEnabledChange
                                  }
                                />
                              }
                              label="사용자 미션 사용"
                            />
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="미션 등장 주기"
                              value={customModeMissionEvery}
                              onChange={handleCustomModeMissionEveryChange}
                              disabled={!customModeMissionsEnabled}
                              helperText={
                                customModeMissionsEnabled
                                  ? `질문 ${customModeMissionEvery}개마다 사용자 미션 카드가 나와요.`
                                  : "사용자 미션을 켜면 질문 몇 개마다 미션이 나올지 고를 수 있어요."
                              }
                            >
                              {CUSTOM_MODE_MISSION_INTERVAL_OPTIONS.map(
                                (count) => (
                                  <MenuItem key={count} value={count}>
                                    질문 {count}개마다
                                  </MenuItem>
                                ),
                              )}
                            </TextField>
                          </Stack>
                        </Paper>
                      )}
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        sx={{ flexWrap: "wrap", pt: 0.5 }}
                      >
                        {questionBombModes.map((mode) => (
                          <Button
                            key={mode.id}
                            size="small"
                            variant={
                              mode.id === selectedModeId
                                ? "contained"
                                : "outlined"
                            }
                            color={mode.accentColor}
                            onClick={() => handleModeChange(mode.id)}
                            sx={{
                              flex: {
                                xs: "1 1 calc(50% - 8px)",
                                sm: "1 1 calc(33.33% - 8px)",
                              },
                              minWidth: 0,
                            }}
                          >
                            {mode.settingsOnly
                              ? "사용자 설정"
                              : mode.shortLabel}
                          </Button>
                        ))}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        사용자 설정 모드는 여기에서만 선택되고, 직접 추가한
                        질문과 미션만 사용합니다.
                      </Typography>
                    </Stack>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 3, display: "none" }}
                  >
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        사용자 정의 질문 추가
                      </Typography>

                      <TextField
                        select
                        fullWidth
                        label="적용 모드"
                        value={customModeId}
                        sx={{ display: "none" }}
                        onChange={(event) =>
                          setCustomModeId(
                            event.target.value as QuestionBombModeId,
                          )
                        }
                      >
                        {questionBombModes.map((mode) => (
                          <MenuItem key={mode.id} value={mode.id}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        fullWidth
                        label="카테고리"
                        value={customCategory}
                        sx={{ display: "none" }}
                        onChange={(event) =>
                          setCustomCategory(event.target.value)
                        }
                        helperText="예: 우리끼리, 회사 밈, 연애 토크"
                      />

                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="질문"
                        value={customText}
                        sx={{ display: "none" }}
                        error={Boolean(customFormError)}
                        helperText={
                          customFormError ||
                          "추가한 질문은 바로 저장되고, 현재 모드라면 남은 순서에도 섞여 들어갑니다."
                        }
                        onChange={(event) => {
                          setCustomText(event.target.value);
                          if (customFormError) setCustomFormError("");
                        }}
                      />

                      <TextField
                        fullWidth
                        label="카테고리 (선택)"
                        value={customCategory}
                        onChange={(event) =>
                          setCustomCategory(event.target.value)
                        }
                        helperText="비워두면 카테고리 칩 없이 저장돼요."
                      />

                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="질문 내용"
                        value={customText}
                        error={Boolean(customFormError)}
                        helperText={
                          customFormError ||
                          "추가한 질문은 사용자 설정 모드에서만 보여요."
                        }
                        onChange={(event) => {
                          setCustomText(event.target.value);
                          if (customFormError) setCustomFormError("");
                        }}
                      />

                      <Button
                        variant="contained"
                        onClick={handleAddCustomQuestion}
                      >
                        사용자 정의 질문 추가
                      </Button>
                    </Stack>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 3, display: "none" }}
                  >
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        사용자 정의 미션 추가
                      </Typography>

                      <TextField
                        fullWidth
                        label="미션 제목"
                        value={customMissionTitle}
                        onChange={(event) => {
                          setCustomMissionTitle(event.target.value);
                          if (customMissionFormError) {
                            setCustomMissionFormError("");
                          }
                        }}
                      />

                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="미션 설명"
                        value={customMissionDescription}
                        error={Boolean(customMissionFormError)}
                        helperText={
                          customMissionFormError ||
                          "추가한 미션은 사용자 설정 모드에서만 등장해요."
                        }
                        onChange={(event) => {
                          setCustomMissionDescription(event.target.value);
                          if (customMissionFormError) {
                            setCustomMissionFormError("");
                          }
                        }}
                      />

                      <TextField
                        fullWidth
                        label="메모 (선택)"
                        value={customMissionNote}
                        onChange={(event) =>
                          setCustomMissionNote(event.target.value)
                        }
                        helperText="짧은 규칙이나 분위기 메모를 남겨두면 좋아요."
                      />

                      <Button
                        variant="contained"
                        color="warning"
                        onClick={handleAddCustomMission}
                      >
                        사용자 정의 미션 추가
                      </Button>
                    </Stack>
                  </Paper>

                  <Stack spacing={1.25}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      즐겨찾기 질문
                    </Typography>

                    {favoriteQuestions.length > 0 ? (
                      favoriteQuestions.map((question) => (
                        <Paper
                          key={question.id}
                          variant="outlined"
                          sx={{ p: 1.75, borderRadius: 2 }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={1}
                          >
                            <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                              <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                sx={singleLineChipRowSx}
                              >
                                <Chip
                                  size="small"
                                  label={
                                    getModeById(question.modeId).shortLabel
                                  }
                                />
                                {question.category.trim().length > 0 && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={question.category}
                                  />
                                )}
                                {"createdAt" in question && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label="내 질문"
                                  />
                                )}
                              </Stack>
                              <Typography variant="body2">
                                {question.text}
                              </Typography>
                            </Stack>

                            <IconButton
                              aria-label="즐겨찾기 제거"
                              onClick={() => removeFavorite(question.id)}
                              sx={{ color: "warning.main" }}
                            >
                              <BookmarkIcon />
                            </IconButton>
                          </Stack>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        아직 저장한 질문이 없어요. 마음에 드는 질문이 나오면
                        북마크를 눌러두세요.
                      </Typography>
                    )}
                  </Stack>

                  <Accordion
                    expanded={isCustomQuestionLibraryExpanded}
                    onChange={(_, expanded) =>
                      setIsCustomQuestionLibraryExpanded(expanded)
                    }
                    disableGutters
                    sx={nestedLibraryAccordionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack spacing={0.25}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          내 질문 보관함
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          추가한 질문 {customQuestions.length}개, 질문 추가
                          폼을 관리합니다.
                        </Typography>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Stack spacing={1.5}>
                        <Accordion
                          expanded={isCustomQuestionListExpanded}
                          onChange={(_, expanded) =>
                            setIsCustomQuestionListExpanded(expanded)
                          }
                          disableGutters
                          sx={nestedLibraryAccordionSx}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack spacing={0.25}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                              >
                                추가한 질문 리스트
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {customQuestions.length}개 질문이 저장되어
                                있어요.
                              </Typography>
                            </Stack>
                          </AccordionSummary>

                          <AccordionDetails>
                            {customQuestions.length > 0 ? (
                              <Stack spacing={1.25}>
                                {customQuestions.map((question) => (
                                  <Paper
                                    key={question.id}
                                    variant="outlined"
                                    sx={{ p: 1.75, borderRadius: 2 }}
                                  >
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="flex-start"
                                      spacing={1}
                                    >
                                      <Stack
                                        spacing={0.75}
                                        sx={{ minWidth: 0 }}
                                      >
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          useFlexGap
                                          sx={singleLineChipRowSx}
                                        >
                                          <Chip
                                            size="small"
                                            label={
                                              getModeById(question.modeId)
                                                .shortLabel
                                            }
                                          />
                                          {question.category.trim().length >
                                            0 && (
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={question.category}
                                            />
                                          )}
                                        </Stack>
                                        <Typography variant="body2">
                                          {question.text}
                                        </Typography>
                                      </Stack>

                                      <IconButton
                                        aria-label="사용자 정의 질문 삭제"
                                        onClick={() =>
                                          handleRemoveCustomQuestion(
                                            question.id,
                                          )
                                        }
                                        sx={{ color: "text.secondary" }}
                                      >
                                        <DeleteOutlineIcon />
                                      </IconButton>
                                    </Stack>
                                  </Paper>
                                ))}
                              </Stack>
                            ) : (
                              <Typography color="text.secondary">
                                아직 직접 추가한 질문이 없어요. 우리 모임 전용
                                질문을 쌓아두면 다음 플레이가 더 빨라집니다.
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>

                        <Accordion
                          expanded={isCustomQuestionFormExpanded}
                          onChange={(_, expanded) =>
                            setIsCustomQuestionFormExpanded(expanded)
                          }
                          disableGutters
                          sx={nestedLibraryAccordionSx}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack spacing={0.25}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                              >
                                사용자 질문 추가 인풋모음
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                사용자 설정 모드에서 쓸 질문을 추가합니다.
                              </Typography>
                            </Stack>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Stack spacing={1.5}>
                              <AddSectionHeader
                                infoId="customQuestion"
                                title="사용자 정의 질문 추가"
                                tooltipText="추가를 눌러도 현재 카드로 바로 열리지 않고, 남은 질문 대기열에 섞여 들어가요."
                                openTooltipId={openSectionInfoTooltipId}
                                onOpen={openSectionInfoTooltip}
                                onClose={closeSectionInfoTooltip}
                                onToggle={toggleSectionInfoTooltip}
                              />

                              <TextField
                                fullWidth
                                label="카테고리 (선택)"
                                value={customCategory}
                                onChange={(event) =>
                                  setCustomCategory(event.target.value)
                                }
                                helperText="비워두면 카테고리 칩 없이 저장돼요."
                              />

                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="질문 내용"
                                value={customText}
                                error={Boolean(customFormError)}
                                helperText={
                                  customFormError ||
                                  "추가한 질문은 사용자 설정 모드에서만 보여요."
                                }
                                onChange={(event) => {
                                  setCustomText(event.target.value);
                                  if (customFormError) setCustomFormError("");
                                }}
                              />

                              <Button
                                variant="contained"
                                onClick={handleAddCustomQuestion}
                              >
                                사용자 정의 질문 추가
                              </Button>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={isCustomMissionLibraryExpanded}
                    onChange={(_, expanded) =>
                      setIsCustomMissionLibraryExpanded(expanded)
                    }
                    disableGutters
                    sx={nestedLibraryAccordionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack spacing={0.25}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          내 미션 보관함
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          추가한 미션 {customMissions.length}개, 미션 추가
                          폼을 관리합니다.
                        </Typography>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Stack spacing={1.5}>
                        <Accordion
                          expanded={isCustomMissionListExpanded}
                          onChange={(_, expanded) =>
                            setIsCustomMissionListExpanded(expanded)
                          }
                          disableGutters
                          sx={nestedLibraryAccordionSx}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack spacing={0.25}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                              >
                                추가한 미션 리스트
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {customMissions.length}개 미션이 저장되어
                                있어요.
                              </Typography>
                            </Stack>
                          </AccordionSummary>

                          <AccordionDetails>
                            {customMissions.length > 0 ? (
                              <Stack spacing={1.25}>
                                {customMissions.map((mission) => (
                                  <Paper
                                    key={mission.id}
                                    variant="outlined"
                                    sx={{ p: 1.75, borderRadius: 2 }}
                                  >
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="flex-start"
                                      spacing={1}
                                    >
                                      <Stack
                                        spacing={0.75}
                                        sx={{ minWidth: 0 }}
                                      >
                                        <Chip
                                          size="small"
                                          color="warning"
                                          label={CUSTOM_MISSION_CHIP_LABEL}
                                        />
                                        <Typography fontWeight={600}>
                                          {mission.title}
                                        </Typography>
                                        <Typography variant="body2">
                                          {mission.description}
                                        </Typography>
                                        {mission.note.trim().length > 0 && (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {mission.note}
                                          </Typography>
                                        )}
                                      </Stack>

                                      <IconButton
                                        aria-label="사용자 정의 미션 삭제"
                                        onClick={() =>
                                          handleRemoveCustomMission(mission.id)
                                        }
                                        sx={{ color: "text.secondary" }}
                                      >
                                        <DeleteOutlineIcon />
                                      </IconButton>
                                    </Stack>
                                  </Paper>
                                ))}
                              </Stack>
                            ) : (
                              <Typography color="text.secondary">
                                아직 직접 추가한 미션이 없어요. 사용자 설정
                                모드에서 미션 카드도 보고 싶다면 하나 만들어 두면
                                좋아요.
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>

                        <Accordion
                          expanded={isCustomMissionFormExpanded}
                          onChange={(_, expanded) =>
                            setIsCustomMissionFormExpanded(expanded)
                          }
                          disableGutters
                          sx={nestedLibraryAccordionSx}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack spacing={0.25}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                              >
                                사용자 미션 추가 인풋모음
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                사용자 설정 모드에서 쓸 미션을 추가합니다.
                              </Typography>
                            </Stack>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Stack spacing={1.5}>
                              <AddSectionHeader
                                infoId="customMission"
                                title="사용자 정의 미션 추가"
                                tooltipText="추가를 눌러도 현재 카드로 바로 열리지 않고, 남은 미션 대기열에 섞여 들어가요."
                                openTooltipId={openSectionInfoTooltipId}
                                onOpen={openSectionInfoTooltip}
                                onClose={closeSectionInfoTooltip}
                                onToggle={toggleSectionInfoTooltip}
                              />

                              <TextField
                                fullWidth
                                label="미션 제목"
                                value={customMissionTitle}
                                onChange={(event) => {
                                  setCustomMissionTitle(event.target.value);
                                  if (customMissionFormError) {
                                    setCustomMissionFormError("");
                                  }
                                }}
                              />

                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="미션 설명"
                                value={customMissionDescription}
                                error={Boolean(customMissionFormError)}
                                helperText={
                                  customMissionFormError ||
                                  "추가한 미션은 사용자 설정 모드에서만 등장해요."
                                }
                                onChange={(event) => {
                                  setCustomMissionDescription(
                                    event.target.value,
                                  );
                                  if (customMissionFormError) {
                                    setCustomMissionFormError("");
                                  }
                                }}
                              />

                              <TextField
                                fullWidth
                                label="메모 (선택)"
                                value={customMissionNote}
                                onChange={(event) =>
                                  setCustomMissionNote(event.target.value)
                                }
                                helperText="짧은 규칙이나 분위기 메모를 남겨두면 좋아요."
                              />

                              <Button
                                variant="contained"
                                color="warning"
                                onClick={handleAddCustomMission}
                              >
                                사용자 정의 미션 추가
                              </Button>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              {!isCompleted && (
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  onClick={resetSession}
                  disabled={
                    !currentCard && questionCursor === 0 && missionCount === 0
                  }
                >
                  전체 재시작
                </Button>
              )}

              <Button fullWidth size="large" onClick={() => navigate("/list")}>
                게임 목록으로
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
}
